import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Mail, Search, Filter, Paperclip, Calendar, User, Building2,
  AlertTriangle, ChevronRight, Download, Link2, Clock, Tag,
  FileText, MoreHorizontal, Brain, RefreshCw, Loader2
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import EmailDropZone from './EmailDropZone';
import EmailDetailModal from './EmailDetailModal';

const IMPACT_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-green-100 text-green-700 border-green-200',
  none: 'bg-slate-100 text-slate-500 border-slate-200',
};

const CorrespondenceTab = ({ 
  projectId, 
  moduleType = null, 
  moduleEntityId = null,
  title = "Correspondence"
}) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterHasAttachments, setFilterHasAttachments] = useState('all');
  const [filterNeedsAction, setFilterNeedsAction] = useState('all');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [stats, setStats] = useState(null);
  const [analyzingIds, setAnalyzingIds] = useState(new Set());
  const [threadAnalysis, setThreadAnalysis] = useState(null);
  const [analyzingThread, setAnalyzingThread] = useState(false);

  const loadEmails = useCallback(async () => {
    try {
      setLoading(true);
      const params = { project_id: projectId };
      if (moduleType) params.module_type = moduleType;
      if (moduleEntityId) params.module_entity_id = moduleEntityId;
      if (filterHasAttachments !== 'all') params.has_attachments = filterHasAttachments === 'yes';
      if (filterNeedsAction !== 'all') params.needs_action = filterNeedsAction === 'yes';
      if (searchTerm) params.search = searchTerm;
      
      const response = await api.get('/correspondence/emails', { params });
      setEmails(response.data);
    } catch (error) {
      toast.error('Failed to load correspondence');
    } finally {
      setLoading(false);
    }
  }, [projectId, moduleType, moduleEntityId, filterHasAttachments, filterNeedsAction, searchTerm]);

  const loadStats = useCallback(async () => {
    try {
      // Build params to get module-specific stats if filtering by module
      let url = `/correspondence/stats/${projectId}`;
      if (moduleType && moduleEntityId) {
        url += `?module_type=${moduleType}&module_entity_id=${moduleEntityId}`;
      }
      const response = await api.get(url);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  }, [projectId, moduleType, moduleEntityId]);

  useEffect(() => {
    loadEmails();
    loadStats();
  }, [loadEmails, loadStats]);

  const handleImportComplete = () => {
    loadEmails();
    loadStats();
    toast.success('Emails imported successfully');
  };

  const triggerAnalysis = async (emailId, e) => {
    e.stopPropagation();
    setAnalyzingIds(prev => new Set([...prev, emailId]));
    try {
      await api.post(`/correspondence/emails/${emailId}/analyze`);
      toast.success('AI analysis complete');
      await loadEmails();
    } catch (error) {
      toast.error('Failed to analyze email');
    } finally {
      setAnalyzingIds(prev => {
        const next = new Set(prev);
        next.delete(emailId);
        return next;
      });
    }
  };

  const handleAnalyzeThread = async () => {
    if (!moduleType || !moduleEntityId) return;
    setAnalyzingThread(true);
    setThreadAnalysis(null);
    try {
      const res = await api.correspondence.analyzeModuleCorrespondence(moduleType, moduleEntityId);
      setThreadAnalysis(res.data);
      toast.success('AI thread analysis complete');
    } catch (error) {
      toast.error('AI analysis failed');
    } finally {
      setAnalyzingThread(false);
    }
  };

  const getImpactLevel = (email) => {
    const score = email.ai_impact?.delay_risk_score || 0;
    if (score >= 70) return 'high';
    if (score >= 40) return 'medium';
    if (score > 0) return 'low';
    return 'none';
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500">
            {emails.length} emails • {stats?.action_required_count || 0} need action
          </p>
        </div>
        <div className="flex items-center gap-2">
          {moduleType && moduleEntityId && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAnalyzeThread}
              disabled={analyzingThread || emails.length === 0}
              data-testid="ai-analyze-correspondence-btn"
            >
              {analyzingThread ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Analyzing...</>
              ) : (
                <><Brain className="h-4 w-4 mr-1" /> AI Analyze Thread</>
              )}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={loadEmails}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* AI Thread Analysis Result */}
      {threadAnalysis && (
        <Card className="bg-indigo-50 border-indigo-200" data-testid="ai-correspondence-analysis-result">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-indigo-600" />
                <span className="text-sm font-semibold text-indigo-700">AI Correspondence Analysis</span>
              </div>
              <div className="flex items-center gap-2">
                {threadAnalysis.summary?.total_messages && (
                  <span className="text-xs text-indigo-500">{threadAnalysis.summary.total_messages} messages analyzed</span>
                )}
                <Button variant="ghost" size="sm" onClick={() => setThreadAnalysis(null)} className="h-6 w-6 p-0">
                  <span className="text-xs">×</span>
                </Button>
              </div>
            </div>
            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap text-sm leading-relaxed">
              {threadAnalysis.analysis}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          <Card className="bg-slate-50">
            <CardContent className="p-3">
              <p className="text-xs text-slate-500">Total Emails</p>
              <p className="text-xl font-bold">{stats.total_emails}</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50">
            <CardContent className="p-3">
              <p className="text-xs text-amber-600">Unassigned</p>
              <p className="text-xl font-bold text-amber-700">{stats.unassigned_count}</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50">
            <CardContent className="p-3">
              <p className="text-xs text-blue-600">Action Required</p>
              <p className="text-xl font-bold text-blue-700">{stats.action_required_count}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50">
            <CardContent className="p-3">
              <p className="text-xs text-red-600">High Risk</p>
              <p className="text-xl font-bold text-red-700">{stats.high_risk_count}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Drop Zone */}
      <EmailDropZone
        projectId={projectId}
        moduleType={moduleType}
        moduleEntityId={moduleEntityId}
        onImportComplete={handleImportComplete}
      />

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search emails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={filterHasAttachments} onValueChange={setFilterHasAttachments}>
          <SelectTrigger className="w-[150px]">
            <Paperclip className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Attachments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="yes">Has Attachments</SelectItem>
            <SelectItem value="no">No Attachments</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterNeedsAction} onValueChange={setFilterNeedsAction}>
          <SelectTrigger className="w-[150px]">
            <AlertTriangle className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="yes">Needs Action</SelectItem>
            <SelectItem value="no">No Action</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Email List */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-8 text-slate-500">Loading emails...</div>
        ) : emails.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No correspondence yet</p>
            <p className="text-sm">Drag & drop email files above to import</p>
          </div>
        ) : (
          emails.map((email) => (
            <Card 
              key={email.id}
              className={`cursor-pointer hover:shadow-md transition-shadow ${
                email.needs_action ? 'border-l-4 border-l-amber-500' : ''
              }`}
              onClick={() => setSelectedEmail(email)}
              data-testid={`email-card-${email.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Impact indicator */}
                  <div className={`
                    w-2 h-full min-h-[60px] rounded-full
                    ${getImpactLevel(email) === 'high' ? 'bg-red-500' :
                      getImpactLevel(email) === 'medium' ? 'bg-amber-500' :
                      getImpactLevel(email) === 'low' ? 'bg-green-500' : 'bg-slate-200'}
                  `} />
                  
                  <div className="flex-1 min-w-0">
                    {/* Header row */}
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-slate-900 line-clamp-1">{email.subject}</h4>
                        <p className="text-sm text-slate-500">
                          From: {email.from_name || email.from_email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <Calendar className="h-3 w-3" />
                        {formatDate(email.sent_at)}
                      </div>
                    </div>

                    {/* AI Summary */}
                    {email.ai_summary && (
                      <p className="text-sm text-slate-600 mb-2 line-clamp-2 bg-slate-50 p-2 rounded">
                        <Brain className="h-3 w-3 inline mr-1 text-indigo-500" />
                        {email.ai_summary}
                      </p>
                    )}

                    {/* Tags and badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {email.has_attachments && (
                        <Badge variant="outline" className="text-xs">
                          <Paperclip className="h-3 w-3 mr-1" />
                          {email.attachments?.length || 0} files
                        </Badge>
                      )}
                      
                      {email.needs_action && (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Action Required
                        </Badge>
                      )}
                      
                      {email.ai_impact?.delay_risk_score > 0 && (
                        <Badge variant="outline" className={`text-xs ${IMPACT_COLORS[getImpactLevel(email)]}`}>
                          Risk: {email.ai_impact.delay_risk_score}%
                        </Badge>
                      )}
                      
                      {email.ai_tags?.slice(0, 3).map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      
                      {!email.ai_analyzed && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-xs h-6 px-2"
                          onClick={(e) => triggerAnalysis(email.id, e)}
                          disabled={analyzingIds.has(email.id)}
                        >
                          {analyzingIds.has(email.id) ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Brain className="h-3 w-3 mr-1" />
                              Analyze
                            </>
                          )}
                        </Button>
                      )}
                      
                      {/* Show linked modules */}
                      {email.module_entity_id && (
                        <Badge 
                          variant="outline" 
                          className="text-xs bg-indigo-50 text-indigo-700 cursor-pointer hover:bg-indigo-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            const links = {
                              'RFI': `/rfis/${email.module_entity_id}`,
                              'SI': `/site-instructions/${email.module_entity_id}`,
                              'DelayEvent': `/events/${email.module_entity_id}`,
                              'Notice': `/notices/${email.module_entity_id}`,
                              'Claim': `/claims/${email.module_entity_id}`
                            };
                            const url = links[email.module_type];
                            if (url) window.open(url, '_blank');
                          }}
                        >
                          <Link2 className="h-3 w-3 mr-1" />
                          {email.module_entity_reference || `${email.module_type}`}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Email Detail Modal */}
      {selectedEmail && (
        <EmailDetailModal
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
          onUpdate={() => {
            loadEmails();
            setSelectedEmail(null);
          }}
          projectId={projectId}
        />
      )}
    </div>
  );
};

export default CorrespondenceTab;
